import {
  BaseEquipment, BenchAnchorPoint, Equipment, GymTypes, SmallBodyGroupType,
} from 'fitbeat.models';
import { WarmupWarmdownConfig } from '../../config/WarmupwarmdownConfig';
import { AreaCategory, staticFunctionalAreaZones, ZoneMapper } from '../../config/zone-mappings';
import { HomeQuarterCalculator } from '../../logic/workoutselection/home/homequartercalculator'; // fix cycle
import {
  BigBodyGroup, CardioOrResistance, Exercise, ExerciseFormat, Level,
} from '../exercise';
import { Zone } from '../gymschedule';
import { WorkoutSettings } from '../user/workoutsettings';
import { InternalWorkoutReview, InternalWorkoutReviewStatus } from '../workoutsession';
import {
  FilterHelper,
  canChangeToAnchorBandBenchRule,
  canChangeToBenchRuleForSuperset,
  canEliminateToBodyweightRule,
  exerciseBenchSetupValidation,
  exerciseFilterElementsMatch,
  hasSameEquipmentWeight,
  hasSameEquipmentWeightForSuperset,
  hasValidEquipmentElimination,
  hasValidEquipmentEliminationForSuperSet,
  isValidFormatForFiltering,
} from './exerciseSelectionFilterHelpers';
import { ExerciseSelection } from './exerciseselection';
import { WorkoutSelectionReview } from './workoutselectionreview';
import { isHomeBoxingWorkout } from '../../logic/workoutselection/boxing-zone-helper';
import {
  isStaticWorkout,
  isStudioDumbbellsExercise,
} from '../../logic/workoutselection/static-zone-helper';

export enum WorkoutSelectionStatus {
  AwatingReview = 'AwatingReview',
  WorkoutIncomplete = 'WorkoutIncomplete',
  ReviewFailed = 'ReviewFailed',
  ReviewPassed = 'ReviewPassed',
  DisagreeWithFail = 'DisagreeWithFail',
  ExerciseFaulty = 'ExerciseFaulty',
  Complete = 'Complete',
}

export interface IfilterRuleStatus {
  isValid: boolean;
  message?: string;
}
// This is a pregenerated workout, not tied to a specific session or user yet
export class WorkoutSelection {
  public static refNr(
    workoutSequenceDate: number,
    settings: WorkoutSettings,
    gymType: GymTypes = GymTypes.Indoor,
    areaCategory?: AreaCategory,
  ): string {
    const gymTypePrefix = gymType === GymTypes.Indoor ? '' : '_';
    return `${workoutSequenceDate}${gymTypePrefix}${WorkoutSelection.refSelectionString(
      settings,
      gymType,
      undefined,
      areaCategory
    )}`;
  }

  public static refSelectionString(
    settings: WorkoutSettings,
    gymType: GymTypes = GymTypes.Indoor,
    shouldIncludeUnderscore: boolean = true,
    areaCategory?: AreaCategory,
  ) {
    const gymTypeString = gymType === GymTypes.Indoor ? '' : `${gymType.toLowerCase()}`;
    return `${gymTypeString}${shouldIncludeUnderscore ? '_' : ''}${WorkoutSelection.refSettingsString(settings, areaCategory)}`;
  }

  public static refSettingsString(
    settings: WorkoutSettings,
    areaCategory?: AreaCategory,
  ): string {
    const defaultFullBody = [BigBodyGroup.BackAndArms, BigBodyGroup.Lower, BigBodyGroup.Upper];
    settings.threeBodyGroups = !settings.threeBodyGroups
      || settings.threeBodyGroups.length === 0 ? defaultFullBody : settings.threeBodyGroups;
    const isEliteEquipment = BaseEquipment.isEliteEquipment(settings.equipment);
    const noBagZone = areaCategory === 'calisthenic' || (settings.allowBagContact !== true && !isEliteEquipment);
    const allowedContactString =  noBagZone ? '_nobag' : '';
    const equipment = this.getEquipmentString(settings.equipment);
    return `${settings.cardioOrResistance}_${
      settings.level}_${
      settings.threeBodyGroups.sort().join('-')
    }${allowedContactString
    }${equipment}`;
  }

  public static areAllExercisesApproved(workoutSelection: WorkoutSelection): boolean {
    const notApprovedExercises = workoutSelection.exerciseSelections.filter((exerciseSelection: ExerciseSelection) => !exerciseSelection.approved);

    return notApprovedExercises.length === 0;
  }

  public static exerciseSelectionsForActiveZoneNumbers(workoutSelection: WorkoutSelection): ExerciseSelection[] {
    const allValidZoneTypeNumbers = ZoneMapper.getAllZoneTypeNumbers();
    const validExerciseSelections = workoutSelection.exerciseSelections
      .filter((es) => allValidZoneTypeNumbers.indexOf(es.zone) !== -1);
    return validExerciseSelections;
  }

  public static isUsable(workoutSelection: WorkoutSelection, areaCategory?: AreaCategory): boolean {
    return workoutSelection.internalWorkoutReview.reviewStatus === InternalWorkoutReviewStatus.Pass
      && WorkoutSelection.areAllExercisesApproved(workoutSelection)
      && WorkoutSelection.areAllExercisesOrZonesNotFaulty(workoutSelection, areaCategory);
  }

  public static isExerciseNotFaultyBasedOnRestOfWorkout(
    exercise: Exercise,
    exerciseSelections: ExerciseSelection[],
  ): { exerciseHasDuplicates: boolean; message?: string } {
    const workoutSelections = exerciseSelections.map((temp: ExerciseSelection) => [
      temp.exercise,
      temp.alternateExercise,
    ]);

    for (const singleWorkoutSelections of workoutSelections) {
      const searchForExerciseInZone = singleWorkoutSelections.filter((singleExercise: Exercise) => singleExercise && singleExercise.uniqueReferenceNumber === exercise.uniqueReferenceNumber);

      if (searchForExerciseInZone.length >= 1) {
        return { exerciseHasDuplicates: true, message: 'Exercise has duplicates in zone' };
      }
    }
    return { exerciseHasDuplicates: false };
  }

  public static areAllStudioExercisesNotFaulty(workoutSelection: WorkoutSelection, areaCategory?: AreaCategory): {
    status: boolean; errorMessages: string[]
  } {
    const exerciseSelectionsForActiveZoneNumbers = WorkoutSelection.exerciseSelectionsForActiveZoneNumbers(workoutSelection);
    const selectionsWithoutWarmups = exerciseSelectionsForActiveZoneNumbers.filter((exerciseSelection: ExerciseSelection) => !(WarmupWarmdownConfig.isWarmupOrWarmdown(exerciseSelection.format)));

    const exerciseIsNotFaulty: { status: boolean, errorMessages: string[] } = { status: true, errorMessages: [] };
    let updatedErrorMessages: string[] = [];
    for (const exerciseSelection of selectionsWithoutWarmups) {
      updatedErrorMessages = WorkoutSelection.validateIndoorExerciseSelection(
        exerciseSelection.exercise,
        exerciseIsNotFaulty,
        updatedErrorMessages,
        selectionsWithoutWarmups,
      );

      if (exerciseSelection.format === ExerciseFormat.HeroZonePunches) {
        if (exerciseSelection.alternateExercise || exerciseSelection.secondAlternateExercise
            || exerciseSelection.thirdAlternateExercise) {
          exerciseIsNotFaulty.status = false;
          updatedErrorMessages = this.addErrorToErrorList(`${exerciseSelection.format} should only have a single exercise`, exerciseIsNotFaulty.errorMessages);
          exerciseIsNotFaulty.errorMessages = updatedErrorMessages;
        }
        continue;
      }

      updatedErrorMessages = WorkoutSelection.validateIndoorExerciseSelection(
        exerciseSelection.alternateExercise,
        exerciseIsNotFaulty,
        updatedErrorMessages,
        selectionsWithoutWarmups,
      );
      if (areaCategory !== 'circuit') continue;
      updatedErrorMessages = WorkoutSelection.validateIndoorExerciseSelection(
        exerciseSelection.secondAlternateExercise,
        exerciseIsNotFaulty,
        updatedErrorMessages,
        selectionsWithoutWarmups,
        true,
      );
      updatedErrorMessages = WorkoutSelection.validateIndoorExerciseSelection(
        exerciseSelection.thirdAlternateExercise,
        exerciseIsNotFaulty,
        updatedErrorMessages,
        selectionsWithoutWarmups,
        true,
      );
    }
    if (updatedErrorMessages.length >= 1) {
      exerciseIsNotFaulty.status = false;
      exerciseIsNotFaulty.errorMessages = updatedErrorMessages;
    }
    return exerciseIsNotFaulty;
  }

  public static areAllExercisesNotFaulty(workoutSelection: WorkoutSelection, areaCategory?: AreaCategory): boolean {
    const exerciseSelectionsForActiveZoneNumbers = WorkoutSelection.exerciseSelectionsForActiveZoneNumbers(workoutSelection);
    const selectionsWithoutWarmups = exerciseSelectionsForActiveZoneNumbers.filter((exerciseSelection: ExerciseSelection) => !(WarmupWarmdownConfig.isWarmupOrWarmdown(exerciseSelection.format)));

    function isFaultyCircuitExercise(exerciseSelection: ExerciseSelection, workoutSelection: WorkoutSelection): boolean | undefined {
      return (
        WorkoutSelection.isExerciseFaulty(exerciseSelection.exercise, exerciseSelection, workoutSelection.settings, workoutSelection.gymType)
        || (exerciseSelection.alternateExercise && WorkoutSelection.isExerciseFaulty(exerciseSelection.alternateExercise, exerciseSelection, workoutSelection.settings, workoutSelection.gymType))
        || (exerciseSelection.secondAlternateExercise && WorkoutSelection.isExerciseFaulty(exerciseSelection.secondAlternateExercise, exerciseSelection, workoutSelection.settings, workoutSelection.gymType))
        || (exerciseSelection.thirdAlternateExercise && WorkoutSelection.isExerciseFaulty(exerciseSelection.thirdAlternateExercise, exerciseSelection, workoutSelection.settings, workoutSelection.gymType))
      );
    }

    function isFaultyStaticExercise(exerciseSelection: ExerciseSelection, workoutSelection: WorkoutSelection): boolean | undefined {
      const first = WorkoutSelection.isExerciseFaulty(exerciseSelection.exercise, exerciseSelection, workoutSelection.settings, workoutSelection.gymType, areaCategory);
      const second = (exerciseSelection.alternateExercise && WorkoutSelection.isExerciseFaulty(exerciseSelection.alternateExercise, exerciseSelection, workoutSelection.settings, workoutSelection.gymType, areaCategory))
      return (
        first
        || second
      );
    }
    let faultyExercises = [];
    if (staticFunctionalAreaZones.includes(areaCategory ?? 'circuit')) {
      faultyExercises = selectionsWithoutWarmups.filter((exerciseSelection: ExerciseSelection) => {
        return isFaultyStaticExercise(exerciseSelection, workoutSelection);
      })
    } else {
      faultyExercises = selectionsWithoutWarmups.filter((exerciseSelection: ExerciseSelection) => {
        return isFaultyCircuitExercise(exerciseSelection, workoutSelection);
      })
    }
    
    const areAllExerciseNotFaulty = faultyExercises.length === 0;
    return areAllExerciseNotFaulty;
  }

  public static areAllExercisesOrZonesNotFaulty(workoutSelection: WorkoutSelection, areaCategory?: AreaCategory): boolean {
    const areAllExerciseNotFaulty = WorkoutSelection.areAllExercisesNotFaulty(workoutSelection, areaCategory);
    let areAllZonesNotFaulty = true;
    if (workoutSelection.gymType === GymTypes.Home) {
      const quarterCalc = new HomeQuarterCalculator();
      const zoneValidation = quarterCalc.calculate(workoutSelection);
      areAllZonesNotFaulty = zoneValidation.errors != null ? zoneValidation.errors.length === 0 : true;
    } else {
      areAllZonesNotFaulty = WorkoutSelection.areAllStudioExercisesNotFaulty(workoutSelection, areaCategory).status;
    }

    return areAllExerciseNotFaulty && areAllZonesNotFaulty;
  }

  public static isExerciseFaulty(
    exercise: Exercise | undefined,
    exerciseSelection: ExerciseSelection,
    workoutSettings: WorkoutSettings,
    gymType: GymTypes,
    areaCategory?: AreaCategory,
  ): boolean {
    const faultReason = WorkoutSelection.exerciseFault(
      exercise,
      exerciseSelection,
      workoutSettings,
      gymType,
      areaCategory,
    );
    return faultReason !== undefined;
  }

  public static exerciseHasBenchEquipment = (exercise: Exercise): boolean => BaseEquipment.containsHomeBench(exercise.equipment);

  public static isBenchAndBandsExercise = (exercise: Exercise) => WorkoutSelection.exerciseHasBenchEquipment(exercise)
      && WorkoutSelection.isBandsEquipmentExercise(exercise);

  public static isBandsEquipmentExercise = (exercise: Exercise) => BaseEquipment.containsHomeBands(exercise.equipment);

  public static exerciseFault(
    exercise: Exercise | undefined,
    exerciseSelection: ExerciseSelection,
    workoutSettings: WorkoutSettings,
    gymType: GymTypes,
    areaCategory?: AreaCategory,
  ): string | undefined {
    const homeBoxingWorkout = isHomeBoxingWorkout(workoutSettings, gymType);
    const exerciseForStaticStudio = isStaticWorkout(gymType, workoutSettings)
      || homeBoxingWorkout;
    const studioDumbbellsExercise = exercise  && isStudioDumbbellsExercise(gymType, exercise);
    if (exercise == null
      || exercise.level == null
      || exercise.definedZones == null
      || exercise.exerciseFormats == null) {
      return undefined; // don't check
    }
    if (exercise.disable) {
      return 'Exercise disabled';
    }
    if (exercise.reviewPassed === false) {
      return 'Review failed or not done';
    }
    if (exercise.cardioOrResistance.indexOf(workoutSettings.cardioOrResistance) < 0
      && workoutSettings.level !== Level.Beginner // TODO why do we have this beginner check?
      && !homeBoxingWorkout
    ) {
      return 'Cardio/resistance not set';
    }
    if (exercise.level.indexOf(workoutSettings.level) < 0) {
      return 'Level not set';
    }
    if (!exerciseForStaticStudio && exercise.definedZones.indexOf(exerciseSelection.zone) < 0) {
      return 'Defined zones not set';
    }

    if (studioDumbbellsExercise && exercise.definedZones.indexOf(exerciseSelection.zone) < 0) {
      return 'Defined zones not set for DB studio exercise';
    }

    if (exercise.exerciseFormats.indexOf(exerciseSelection.format) < 0
      && workoutSettings.level !== Level.Beginner
      && !homeBoxingWorkout
    ) {
      return 'Exercise format does not match';
    }
    if (homeBoxingWorkout && !exercise.exerciseFormats.includes(ExerciseFormat.Boxing)) {
      return 'Exercise format does not include boxing';
    }

    const bodyGroupSinkHole = WorkoutSelection.bodyGroupSinkHoleCheck(exercise, workoutSettings);
    if (bodyGroupSinkHole) {
      return bodyGroupSinkHole;
    }

    const { allowBagContact = true, equipment = [] } = workoutSettings;
    if ((Zone.isBoxingZone(exerciseSelection.zone) && exercise.bagContact !== allowBagContact)) {
      return 'Bag contact does not match setting on boxing zone';
    }

    const exerciseEquipment = exercise.equipment || [];
    const equipmentFromSettings = [...equipment];
    const isEquipmentTheSame = equipmentFromSettings.length > 0
      && exerciseEquipment.every((e) => equipmentFromSettings.indexOf(e) === -1);
    const { bagContact } = exercise;
    const homeBoxingWorkoutWithBag = homeBoxingWorkout && bagContact;

    if (isEquipmentTheSame && !homeBoxingWorkoutWithBag) {
      return 'Equipment does not match settings';
    }

    if (Exercise.isHomeExerciseFormat(exerciseSelection.format)) {
      const equipmentRequiringInstruction = BaseEquipment.getEquipmentRequiringInstructionInfo(exercise.equipment);
      if (equipmentRequiringInstruction && equipmentRequiringInstruction !== Equipment.Dumbbells
        && equipmentRequiringInstruction !== Equipment.Pushbells) {
        const hasEquipmentWeightLevel = !!exercise.equipmentWeightLevel;
        const hasPrimaryMuscleMover = !!exercise.primaryMuscleMover;
        if (!hasEquipmentWeightLevel && !hasPrimaryMuscleMover) {
          return 'Exercise requires Weight Allocation and Primary Mover';
        }
        if (!hasEquipmentWeightLevel) {
          return 'Exercise requires Weight Allocation';
        }
        if (!hasPrimaryMuscleMover) {
          return 'Exercise requires Primary Mover';
        }
      }
    }

    const benchCheckErrorMessage = this.doesHomeBenchSetupMatchFirstExercise(exerciseSelection, exercise);
    if (benchCheckErrorMessage !== undefined) {
      return benchCheckErrorMessage;
    }

    if (Exercise.isHomeExerciseFormat(exerciseSelection.format) && !exerciseForStaticStudio) {
      let exerciseValidationState = null;
      if (exercise.name === exerciseSelection.alternateExercise?.name) {
        exerciseValidationState = WorkoutSelection.validateIfNextExerciseIsAllowedToFollowPrior(
          exerciseSelection.format,
          exercise,
          exerciseSelection.exercise,
          workoutSettings.cardioOrResistance,
        );
      } else if (exercise.name === exerciseSelection.secondAlternateExercise?.name
        && exerciseSelection.alternateExercise) {
        exerciseValidationState = WorkoutSelection.validateIfNextExerciseIsAllowedToFollowPrior(
          exerciseSelection.format,
          exercise,
          exerciseSelection.alternateExercise,
          workoutSettings.cardioOrResistance,
        );
      } else if (exercise.name === exerciseSelection.thirdAlternateExercise?.name
        && exerciseSelection.secondAlternateExercise) {
        exerciseValidationState = WorkoutSelection.validateIfNextExerciseIsAllowedToFollowPrior(
          exerciseSelection.format,
          exercise,
          exerciseSelection.secondAlternateExercise,
          workoutSettings.cardioOrResistance,
        );
      }
      if (exerciseValidationState && !exerciseValidationState.isValid) {
        return exerciseValidationState?.message;
      }
    }

    if (
      exercise.voiceTipReviewStatuses != null
      && Exercise.isHomeExerciseFormat(exerciseSelection.format)
      && !exerciseForStaticStudio
      && !Exercise.isBalanceFormat(exerciseSelection.format)) {
      const failedVoiceTip = exercise.voiceTipReviewStatuses.find((item) => item.passed === false);
      const emptyVoiceTip = exercise.voiceTipReviewStatuses.find((item) => item.passed === null);

      const isBodyWeight = exercise.equipment.includes(Equipment.Bodyweight);
      if (failedVoiceTip) {
        if (failedVoiceTip.ptName === 'Alrieta') {
          return undefined;
        }
        if (!isBodyWeight && failedVoiceTip.ptName === 'Tyson') {
          return undefined;
        }
        return `Voice tip failed review for pt ${failedVoiceTip.ptName}`;
      }

      if (emptyVoiceTip) {
        if (!isBodyWeight && emptyVoiceTip.ptName === 'Tyson') {
          return undefined;
        }

        if (emptyVoiceTip.ptName === 'Alrieta') {
          return undefined;
        }
        if (emptyVoiceTip.ptName === 'Eliza') {
          return undefined;
        }
        return `Voice tip Not available for pt ${emptyVoiceTip.ptName}`;
      }
    }

    const temporaryDisablingAlrietaVoiceTipSinkHold = true
    if (
      exercise.voiceTipReviewStatuses != null
      && Exercise.isHomeExerciseFormat(exerciseSelection.format)
      && Exercise.isBalanceFormat(exerciseSelection.format)
      && !temporaryDisablingAlrietaVoiceTipSinkHold) {

      const failedVoiceTip = exercise.voiceTipReviewStatuses.find((item) => item.passed === false && item.ptName === 'Alrieta');
      const emptyVoiceTip = exercise.voiceTipReviewStatuses.find((item) => item.passed === null && item.ptName === 'Alrieta');
      if (failedVoiceTip) {
        return `Voice tip failed review for pt ${failedVoiceTip.ptName}`;
      }
      if (emptyVoiceTip) {
        return `Voice tip Not available for pt ${emptyVoiceTip.ptName}`;
      }
    }
    return undefined;
  }

  public static getSmallBodyGroupAggregate(workoutSelection: WorkoutSelection) {
    const aggregatedSmallBodyGroup = workoutSelection.exerciseSelections.reduce((aggregation: any, ex: ExerciseSelection) => {
      const { exercise, alternateExercise } = ex;

      exercise.bodyGroups.forEach((sbg: SmallBodyGroupType) => {
        aggregation[sbg] = (aggregation[sbg] || 0) + 1;
      });

      if (alternateExercise) {
        alternateExercise.bodyGroups.forEach((sbg: SmallBodyGroupType) => {
          aggregation[sbg] = (aggregation[sbg] || 0) + 1;
        });
      }

      return aggregation;
    }, {});

    return aggregatedSmallBodyGroup;
  }

  public static getBigBodyGroupAggregate(workoutSelection: WorkoutSelection) {
    const getBodyAggregation = (exercise: Exercise, aggregation: any) => {
      const exerciseBigBodyGroups = Exercise.get3BodyGroups(exercise.bodyGroups);
      exerciseBigBodyGroups.forEach((bg: BigBodyGroup) => {
        aggregation[bg] = (aggregation[bg] || 0) + 1;
      });
    };

    const aggregatedBodyGroups = workoutSelection.exerciseSelections.reduce((aggregation: any, exerciseSelection: ExerciseSelection) => {
      const { exercise, alternateExercise } = exerciseSelection;
      getBodyAggregation(exercise, aggregation);

      if (alternateExercise) {
        getBodyAggregation(alternateExercise, aggregation);
      }

      return aggregation;
    }, {});

    return aggregatedBodyGroups;
  }

  public static isMatchingBenchPosition = (currentExercise: Exercise, singleExercise: Exercise): boolean => {
    if (!singleExercise.homeBenchSetupPosition) {
      return false;
    }

    if (!currentExercise.homeBenchSetupPosition) {
      return false;
    }
    return singleExercise.homeBenchSetupPosition.toString() === currentExercise.homeBenchSetupPosition.toString();
  };

  public static isMatchingBenchAnchorPointOption = (currentExercise: Exercise, singleExercise: Exercise) => {
    if (!singleExercise.homeBenchSetupOptions || singleExercise.homeBenchSetupOptions.length <= 0) {
      return false;
    }
    if (!currentExercise.homeBenchSetupOptions || currentExercise.homeBenchSetupOptions.length <= 0) {
      return false;
    }
    return singleExercise.homeBenchSetupOptions.toString() === currentExercise.homeBenchSetupOptions.toString();
  };

  public static isUniBandExercise = (exercise: Exercise) => {
    if (!exercise.equipment || exercise.equipment.length < 1) {
      return false;
    }
    return BaseEquipment.containsUniversalBand(exercise.equipment);
  };

  public static isAnchorPointSupportingBand = (exercise: Exercise) => {
    if (exercise.homeBenchSetupAnchorPoint
      && exercise.homeBenchSetupAnchorPoint.length
      && !exercise.homeBenchSetupAnchorPoint.includes(BenchAnchorPoint.NONE)) {
      return true;
    }
    return false;
  };

  public static isMatchingBandsAnchorPoint = (currentExercise: Exercise, singleExercise: Exercise) => {
    if (!singleExercise.homeBenchSetupAnchorPoint || singleExercise.homeBenchSetupAnchorPoint.length <= 0) {
      return false;
    }

    if (!currentExercise.homeBenchSetupAnchorPoint || currentExercise.homeBenchSetupAnchorPoint.length <= 0) {
      return false;
    }

    for (const selectedExerciseSingleSetup of currentExercise.homeBenchSetupAnchorPoint) {
      const hasMatchingAnchorPoint = singleExercise.homeBenchSetupAnchorPoint.includes(selectedExerciseSingleSetup);
      return hasMatchingAnchorPoint;
    }
    return false;
  };

  public static doesHomeBenchSetupMatchFirstExercise(
    exerciseSelection: ExerciseSelection,
    currentExercise: Exercise,
  ): string | undefined {
    if (!Exercise.isHomeExerciseFormat(exerciseSelection.format)) {
      return undefined;
    }
    if (!currentExercise) { return undefined; }
    if (!currentExercise.homeBenchSetupOptions || currentExercise.homeBenchSetupOptions.length === 0) {
      return undefined;
    }
    const primaryExercise = exerciseSelection.exercise;
    if (!primaryExercise) { return undefined; }
    if (primaryExercise === currentExercise) { return undefined; }

    if (
      WorkoutSelection.isMatchingBenchPosition(primaryExercise, currentExercise)
        && currentExercise.equipment.length === 1
    ) {
      return undefined;
    }

    const BenchSettingErrorMessage = 'Bench settings does not match first exercise in zone.';
    const BandsAnchorSettingErrorMessage = 'Bands Anchor point does not match first exercise in zone.';
    if (
      WorkoutSelection.isBenchAndBandsExercise(primaryExercise)
        && WorkoutSelection.isBenchAndBandsExercise(currentExercise)
        && WorkoutSelection.isAnchorPointSupportingBand(primaryExercise)
        && WorkoutSelection.isAnchorPointSupportingBand(currentExercise)
    ) {
      const isMatchingBandsAnchorPoint = WorkoutSelection.isMatchingBandsAnchorPoint(
        primaryExercise,
        currentExercise,
      );
      if (!isMatchingBandsAnchorPoint) {
        return BandsAnchorSettingErrorMessage;
      }
    }

    if (Exercise.isQuadFormat(exerciseSelection.format)) {
      if (!primaryExercise.homeBenchSetupPosition && currentExercise.homeBenchSetupPosition) {
        return BenchSettingErrorMessage;
      }
      if (primaryExercise.homeBenchSetupPosition.join(',') !== currentExercise.homeBenchSetupPosition.join(',')) {
        return BenchSettingErrorMessage;
      }
      return undefined;
    }

    if (!primaryExercise.homeBenchSetupOptions && currentExercise.homeBenchSetupOptions) {
      return BenchSettingErrorMessage;
    }

    if (primaryExercise.homeBenchSetupOptions.join(',') !== currentExercise.homeBenchSetupOptions.join(',')) {
      return BenchSettingErrorMessage;
    }
    return undefined;
  }

  public static validateIfNextExerciseIsAllowedToFollowPrior = (
    exerciseFormat: ExerciseFormat,
    nextExercise: Exercise,
    priorExercise: Exercise,
    cardioOrResistance: CardioOrResistance,
  ): IfilterRuleStatus => {
    const validationResult = isValidFormatForFiltering(exerciseFormat);
    if (!validationResult.isValid) {
      return validationResult;
    }
    const SingleExerciseEquipmentEliminatedValidation = WorkoutSelection.ExerciseEliminatedSingleEquipment(
      nextExercise,
      priorExercise,
      exerciseFormat,
      cardioOrResistance,
    );
    return SingleExerciseEquipmentEliminatedValidation;
  };

  public static addErrorToErrorList = (errorMessage: string, errorMessageList: string[]): string[] => {
    if (errorMessageList.indexOf(errorMessage) < 0) {
      errorMessageList.push(errorMessage);
    }
    return errorMessageList;
  };

  public static getRepeatingExerciseGroups(workoutSelection: WorkoutSelection): [string, string[]][] {
    const mappedToGroupZonePositionTree = WorkoutSelection.mapToExerciseGroupZonesAndPostions(workoutSelection);
    const reducedToRepeatingPositions = WorkoutSelection.reduceToRepeatingGroupDescriptions(mappedToGroupZonePositionTree);
    return reducedToRepeatingPositions;
  }

  private static getEquipmentString(
    equipment: Equipment[] = [],
  ): string {
    if (equipment.length > 0) {
      equipment.sort();
      return`_${equipment.join('-')}`;
    }
    return '';
  }

  private static mapToExerciseGroupZonesAndPostions(workoutSelection: WorkoutSelection) {
    const exerciseGroupZonesAndPostions = new Map<string, Map<number, Set<number>>>();
    function addExerciseGroupZonesAndPostions(groupName: string, zone: number, positionInZone: number): void {
      let zonesForGroup = exerciseGroupZonesAndPostions.get(groupName);
      if (zonesForGroup == null) {
        zonesForGroup = new Map<number, Set<number>>();
        exerciseGroupZonesAndPostions.set(groupName, zonesForGroup);
      }

      let positionsForZone = zonesForGroup.get(zone);
      if (positionsForZone == null) {
        positionsForZone = new Set<number>();
        zonesForGroup.set(zone, positionsForZone);
      }
      positionsForZone.add(positionInZone);
    }

    for (const exerciseSelection of workoutSelection.exerciseSelections) {
      const exercises = ExerciseSelection.getExercisesInOrder(exerciseSelection);
      for (let positionInZone = 0; positionInZone < exercises.length; positionInZone++) {
        addExerciseGroupZonesAndPostions(exercises[positionInZone].groupName, exerciseSelection.zone, positionInZone);
      }
    }
    return exerciseGroupZonesAndPostions;
  }

  private static reduceToRepeatingGroupDescriptions(
    exerciseGroupZonesAndPostions: Map<string, Map<number, Set<number>>>,
  ): [string, string[]][] {
    const allGroupPositionDescriptions = new Map<string, Set<string>>();
    const groupNames = exerciseGroupZonesAndPostions.keys();

    for (const groupName of groupNames) {
      const zonesAndPostions = exerciseGroupZonesAndPostions.get(groupName);
      if (zonesAndPostions == null) {
        continue;
      }
      for (const zone of zonesAndPostions.keys()) {
        const positionsInZone = zonesAndPostions.get(zone);
        if (positionsInZone != null) {
          for (const positionInZone of positionsInZone.values()) {
            let groupPositions = allGroupPositionDescriptions.get(groupName);
            if (groupPositions == null) {
              groupPositions = new Set<string>();
              allGroupPositionDescriptions.set(groupName, groupPositions);
            }
            const format = (zoneNr: number, positionCharacter: string) => `${zoneNr}${positionCharacter}`;
            const zonePositionDescription = format(zone, ['a', 'b', 'c', 'd', 'e', 'f'][positionInZone]);
            const repeatInZoneOnPurpose = zonePositionDescription === format(zone, 'a') && groupPositions.has(format(zone, 'b'))
              || zonePositionDescription === format(zone, 'b') && groupPositions.has(format(zone, 'a'))
              || zonePositionDescription === format(zone, 'c') && groupPositions.has(format(zone, 'd'))
              || zonePositionDescription === format(zone, 'd') && groupPositions.has(format(zone, 'c'));

            const ignoredZones = [2, 9]; // 2 and 22 are duplicats, and 9 and 21 - so just remove 2 and 9
            const shouldZoneBeAnalysed = ignoredZones.indexOf(zone) === -1;
            if (!repeatInZoneOnPurpose && shouldZoneBeAnalysed) {
              groupPositions.add(zonePositionDescription);
            }
          }
        }
      }
    }

    const repeatingGroupPositionDescriptions = new Map<string, string[]>();
    allGroupPositionDescriptions.forEach((positionDescriptions, groupName) => {
      if (positionDescriptions.size > 1) {
        repeatingGroupPositionDescriptions.set(groupName, [...positionDescriptions.values()]);
      }
    });

    const sortedRepeatingGroupPositionDescriptions = [...repeatingGroupPositionDescriptions.entries()].sort((a, b) => b[1].length - a[1].length);
    return sortedRepeatingGroupPositionDescriptions;
  }

  private static ExerciseEliminatedSingleEquipment = (
    nextExercise: Exercise,
    priorExercise: Exercise,
    zoneFormat: ExerciseFormat,
    cardioOrResistance: CardioOrResistance,
  ): IfilterRuleStatus => {
    const exerciseMatch = exerciseFilterElementsMatch(nextExercise, priorExercise, cardioOrResistance);
    if (exerciseMatch.isValid) {
      return exerciseMatch;
    }

    if (WorkoutSelection.exerciseHasBenchEquipment(nextExercise)) {
      const hasSameBenchSetup = exerciseBenchSetupValidation(nextExercise, priorExercise);
      if (!hasSameBenchSetup.isValid) {
        return hasSameBenchSetup;
      }
    }

    const changingToBodyweight = canEliminateToBodyweightRule(nextExercise, priorExercise);
    if (changingToBodyweight.isValid) {
      return changingToBodyweight;
    }

    const noneQuadFilterMethods: FilterHelper[] = [
      canChangeToAnchorBandBenchRule,
      hasValidEquipmentElimination,
      hasSameEquipmentWeight,
    ];
    const supersetFilterMethods: FilterHelper[] = [
      canChangeToBenchRuleForSuperset,
      hasValidEquipmentEliminationForSuperSet,
      hasSameEquipmentWeightForSuperset,
    ];
    const filterRulesToUse: FilterHelper[] = Exercise.isQuadFormat(zoneFormat)
      ? supersetFilterMethods : noneQuadFilterMethods;
    for (const singleFilterMethod of filterRulesToUse) {
      const methodResults = singleFilterMethod(nextExercise, priorExercise, cardioOrResistance);
      if (!methodResults.isValid) {
        return methodResults;
      }
    }
    return { isValid: true, message: 'passed all the rules' };
  };

  private static validateIndoorExerciseSelection(
    exercise: Exercise | undefined,
    exerciseIsNotFaulty: { status: boolean; errorMessages: string[]; },
    currentErrorMessages: string[],
    selectionsWithoutWarmups: ExerciseSelection[],
    checkIfIsRepeating?: boolean,
  ): string[] {
    let updatedErrorMessages = [...currentErrorMessages];
    if (!exercise) {
      exerciseIsNotFaulty.status = false;
      updatedErrorMessages = this.addErrorToErrorList(
        'Not all zones have Four valid exercises',
        exerciseIsNotFaulty.errorMessages,
      );
      exerciseIsNotFaulty.errorMessages = updatedErrorMessages;
    } else if (checkIfIsRepeating) {
      const hasRepeatingCheck = this.isExerciseNotFaultyBasedOnRestOfWorkout(exercise, selectionsWithoutWarmups);
      if (hasRepeatingCheck.exerciseHasDuplicates && hasRepeatingCheck.message) {
        exerciseIsNotFaulty.status = false;
        updatedErrorMessages = this.addErrorToErrorList(hasRepeatingCheck.message, exerciseIsNotFaulty.errorMessages);
      }
    }
    return updatedErrorMessages;
  }

  private static bodyGroupSinkHoleCheck = (exercise: Exercise, workoutSettings: WorkoutSettings): string | void => {
    if (exercise.bodyGroups.length >= 1 && !exercise.bagContact) {
      const smallBodyGroups = Exercise.getSmallBodyGroupsFor3BodyGroups(workoutSettings.threeBodyGroups);
      const matchedBodyGroups = exercise.bodyGroups.filter((bodyGroup) => smallBodyGroups.indexOf(bodyGroup) !== -1);
      if (matchedBodyGroups.length <= 0) {
        return 'Exercise does not match Body Groups';
      }
    }
  };

  public uniqueReference: string;

  public workoutSequenceDate: number;

  public settings: WorkoutSettings;

  public gymType: GymTypes;

  public ignoreHomeQuarterErrorsForTesting = false;

  public exerciseSelections = new Array<ExerciseSelection>();

  public internalWorkoutReview: InternalWorkoutReview;

  public powerInternalWorkoutReview: InternalWorkoutReview;

  public workoutSelectionReviewHistory: WorkoutSelectionReview[];

  public constructor(
    workoutSequenceDate: number,
    cardioOrResistance: CardioOrResistance,
    allowBagContact: boolean,
    level: Level,
    threeBodyGroups: string[],
    exerciseSelections: ExerciseSelection[],
    gymType: GymTypes,
    equipment: Equipment[],
  ) {
    this.workoutSequenceDate = workoutSequenceDate;
    this.settings = new WorkoutSettings({
      cardioOrResistance, allowBagContact, level, threeBodyGroups, equipment,
    });
    this.uniqueReference = WorkoutSelection.refNr(this.workoutSequenceDate, this.settings, gymType);
    this.exerciseSelections = exerciseSelections;
    this.internalWorkoutReview = new InternalWorkoutReview();
    this.powerInternalWorkoutReview = new InternalWorkoutReview();
    this.workoutSelectionReviewHistory = [];
    this.gymType = gymType;
  }
}
